import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { get, put } from "../../lib/api";
import type { Depot, PaymentProvider } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { SelectField } from "../ui/Input";

/**
 * Which merchant takes drivers' money at a depot.
 *
 * Its own control, saved on its own, because it is its own permission
 * (`payment_providers.manage`): renaming a site and redirecting its payments are
 * different decisions. Only new orders move — every order already taken refunds
 * through the provider it was paid with.
 */

export interface DepotProviderProps {
  readonly depot: Depot;
  readonly onSaved: (message: string) => void;
}

const NONE = "";

export function DepotProvider({ depot, onSaved }: DepotProviderProps) {
  const queryClient = useQueryClient();
  const current = depot.paymentProvider?.id ?? NONE;
  const [chosen, setChosen] = useState(current);

  const providers = useQuery({
    queryKey: ["payment-providers"],
    queryFn: () => get<PaymentProvider[]>("/payment-providers"),
  });

  const save = useMutation({
    mutationFn: (paymentProviderId: string | null) =>
      put(`/depots/${encodeURIComponent(depot.id)}/payment-provider`, { paymentProviderId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["depots"] });
      void queryClient.invalidateQueries({ queryKey: ["payment-providers"] });
      const label = providers.data?.find((row) => row.id === chosen)?.label;
      onSaved(
        label === undefined
          ? `${depot.name} no longer takes payments.`
          : `${depot.name} now takes new payments through ${label}.`,
      );
    },
  });

  return (
    <form
      aria-label={`Payment provider for ${depot.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (chosen === current || save.isPending) return;
        save.mutate(chosen === NONE ? null : chosen);
      }}
      className="grid grid-cols-1 gap-x-6 gap-y-3 border-t border-rule pt-4 md:grid-cols-2"
    >
      {save.isError ? (
        <div className="md:col-span-2">
          <ErrorNotice error={save.error} />
        </div>
      ) : null}
      {providers.isError ? (
        <div className="md:col-span-2">
          <ErrorNotice error={providers.error} onRetry={() => void providers.refetch()} />
        </div>
      ) : null}
      <SelectField
        label="Payment provider"
        value={chosen}
        onChange={(event) => setChosen(event.target.value)}
        disabled={providers.isPending}
        hint={
          chosen === NONE
            ? "None: drivers cannot start a paid charge at this site."
            : "New orders here are paid through it. Past orders keep theirs."
        }
      >
        <option value={NONE}>None — no payments</option>
        {(providers.data ?? []).map((row) => (
          <option key={row.id} value={row.id}>
            {row.label}
            {row.environment === "sandbox" ? " (sandbox)" : ""}
          </option>
        ))}
      </SelectField>
      <div className="flex items-end">
        <Button
          type="submit"
          disabled={chosen === current || save.isPending}
          pending={save.isPending}
        >
          Change provider
        </Button>
      </div>
    </form>
  );
}
